import type {
  DashboardKpis,
  DashboardOrder,
  DashboardPayload,
  InventoryRow,
  Location,
  Product,
  User,
  VendorItem,
  VendorPurchaseOrder,
  VendorPurchaseOrderLine,
} from "./types";

export function firstName(user: User | null): string {
  const source = String(user?.full_name || user?.username || "there").trim();
  return source.split(/\s+/)[0] || "there";
}

export function greeting(): string {
  const hour = Number(new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    hour12: false,
    timeZone: "Asia/Manila"
  }).format(new Date()));
  if (hour < 12) return "Good morning";
  if (hour < 18) return "Good afternoon";
  return "Good evening";
}

export function taskLaneForRow(row: DashboardOrder): string {
  const key = String(row.next_action_key || "").toLowerCase();
  const action = String(row.next_action || "").toLowerCase();
  const status = String(row.status || "").toLowerCase();
  if (key.includes("contest") || action.includes("contest") || status.includes("contested")) return "Review Delivery Issue";
  if (key.includes("branch_confirm") || action.includes("accept") || action.includes("confirm") || status.includes("awaiting branch")) return "Receive Deliveries";
  if (key.includes("job") || key.includes("fill") || action.includes("fill remaining") || action.includes("create delivery")) return "Fill Orders";
  return "Review Orders";
}

export function actionLabel(row: DashboardOrder): string {
  const lane = taskLaneForRow(row);
  if (lane === "Fill Orders") return "Fill order";
  if (lane === "Receive Deliveries") return "Receive delivery";
  if (lane === "Review Delivery Issue") return "Review issue";
  return "Review order";
}

export function plainNextAction(row: DashboardOrder): string {
  return String(row.next_action || row.status || "Open");
}

export function requestDocumentNumber(row: DashboardOrder): string {
  return String(row.po_number || row.request_number || row.document_number || "");
}

export function requestLocationText(row: DashboardOrder): string {
  return [row.request_code, row.request_name].filter(Boolean).join(" - ");
}

export function requestDateText(value: string | undefined): string {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value.slice(0, 10);
  return new Intl.DateTimeFormat("en-PH", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    timeZone: "Asia/Manila"
  }).format(parsed);
}

export function issueCount(kpis: DashboardKpis, financeClose: DashboardPayload["finance_close"]): number {
  const closeChecks = financeClose?.checks || [];
  const needsCloseReview = closeChecks.filter(check => check.status === "Needs Review").length;
  return Number(kpis.storehub_unmapped || 0) + needsCloseReview;
}

export function canViewCloseChecks(user: User | null): boolean {
  return user?.role === "System Admin" || user?.role === "Super Admin" || user?.role === "Accounting Team";
}

export function formatNumber(value: number | undefined): string {
  return Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });
}

export function locationLabel(location: Location): string {
  return [location.code, location.name].filter(Boolean).join(" - ");
}

export function productLabel(product: Product): string {
  const name = product.report_name || product.name || product.sku || "Unnamed item";
  const unit = product.uom || product.bundle_box || "";
  return unit ? `${name} (${unit})` : name;
}

export function inventoryItemLabel(row: InventoryRow): string {
  return row.product_name || row.sku || "Unnamed item";
}

export function inventoryLocationLabel(row: InventoryRow): string {
  return [row.location_code, row.location_name].filter(Boolean).join(" - ");
}

export function formatQuantity(value: number | undefined): string {
  return Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 4 });
}

export function dateDaysFromNow(days: number): string {
  const next = new Date();
  next.setDate(next.getDate() + days);
  return next.toISOString().slice(0, 10);
}

export function vendorItemLabel(item: VendorItem | VendorPurchaseOrderLine): string {
  const sku = String(item.sku || "").trim();
  const name = String(item.po_item_name || item.product_name || ("name" in item ? item.name : "") || "").trim();
  const unit = item.uom || "unit";
  return `${sku ? `${sku} - ` : ""}${name || "Vendor item"} (${unit})`;
}

export function vendorOrderClosed(order: VendorPurchaseOrder): boolean {
  return ["Received", "Received with Discrepancy", "Closed"].includes(String(order.status || ""));
}

export function peso(value: number | null | undefined): string {
  if (value === null || value === undefined) return "";
  return Number(value || 0).toLocaleString(undefined, { style: "currency", currency: "PHP" });
}

export function compactDateTime(value: string | undefined): string {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit"
  });
}

export function compactDate(value: string | undefined): string {
  if (!value) return "";
  const parsed = new Date(`${value}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric"
  });
}

export function financeStatusColor(status: string | undefined): "green" | "orange" | "red" | "blue" | "grey" {
  const clean = String(status || "").toLowerCase();
  if (["done", "posted", "reconciled", "locked", "green"].some(term => clean.includes(term))) return "green";
  if (["blocked", "needs review", "red"].some(term => clean.includes(term))) return "red";
  if (["open", "ready", "exported", "imported", "yellow"].some(term => clean.includes(term))) return "orange";
  if (clean.includes("sent")) return "blue";
  return "grey";
}

export function readableEventLabel(value: string | undefined): string {
  const clean = String(value || "").replace(/[_-]+/g, " ").trim();
  return clean ? clean.replace(/\b\w/g, match => match.toUpperCase()) : "";
}

export function canUseWasteLoss(user: User | null): boolean {
  return Boolean(user?.tabs.includes("manual_adjustments"));
}

export function canUseInventoryCount(user: User | null): boolean {
  return Boolean(user?.tabs.includes("store_count"));
}

export function canAddWarehouseInventory(user: User | null): boolean {
  return Boolean(user && (
    user.role === "System Admin"
    || user.role === "Super Admin"
    || user.role === "Purchaser"
    || user.role === "Warehouse SL"
    || user.tabs.includes("add_inventory")
  ));
}
